import React, { useState } from 'react';
import { VscSettingsGear, VscSignOut, VscMenu, VscListFlat, VscFiles, VscCalendar, VscCommentDiscussion, VscFeedback } from 'react-icons/vsc';
import { IoBoat, IoBriefcaseOutline, IoCalculatorOutline } from 'react-icons/io5';
import { RiUserLine } from 'react-icons/ri';
import { projectHint } from '../../utils/projectHint';
import './ActivityBar.css';

const getSections = (role) => {
  const base = [
    { id: 'ships',        Icon: IoBoat,                 title: 'Projects / Ships', color: '#4fc1ff' },
    { id: 'lists',        Icon: VscListFlat,             title: 'Lists',            color: '#4ec9b0' },
    { id: 'chat',         Icon: VscCommentDiscussion,    title: 'Chat',             color: '#56b6c2' },
    { id: 'files',        Icon: VscFiles,               title: 'Files',            color: '#e5c07b' },
    { id: 'job-packages', Icon: IoBriefcaseOutline,      title: 'Job Packages',     color: '#f0a04b' },
    { id: 'planning',     Icon: VscCalendar,             title: 'Planning',         color: '#98c379' },
  ];
  // Clients only see projects, lists, files and planning
  if (role === 'client') return base.filter(s => !['chat', 'job-packages'].includes(s.id));
  if (['admin', 'engineer', 'manager', 'foreman'].includes(role)) {
    base.push({ id: 'quotes', Icon: IoCalculatorOutline, title: 'Quotes', color: '#c586c0' });
    base.push({ id: 'users', Icon: RiUserLine, title: 'Users', color: '#dcdcaa' });
  }
  if (role === 'admin') {
    base.push({ id: 'feedback', Icon: VscFeedback, title: 'Feedback & Bugs', color: '#e06c75' });
  }
  return base;
};

function SectionBtn({ id, Icon, title, color, active, onClick, badge, subLabel }) {
  return (
    <button
      className={`activity-bar-btn${active ? ' active' : ''}${subLabel ? ' has-sublabel' : ''}`}
      title={title}
      onClick={onClick}
      style={{ '--section-color': color }}
    >
      <Icon size={subLabel ? 20 : 24} style={{ color: active ? color : undefined }} />
      {subLabel && <span className="activity-bar-sublabel">{subLabel}</span>}
      {badge > 0 && <span className="activity-badge">{badge > 99 ? '99+' : badge}</span>}
    </button>
  );
}

function ActivityBar({ activeSection, onSectionChange, onLogout, onOpenSettings, role, totalChatUnread, jobPkgUnread, feedbackCount, activeProject }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const SECTIONS = getSections(role);
  const MOBILE_MAIN = SECTIONS.slice(0, 5);
  const MOBILE_OVERFLOW = SECTIONS.slice(5);

  const getBadge = (id) => {
    if (id === 'chat')         return totalChatUnread  || 0;
    if (id === 'job-packages') return jobPkgUnread     || 0;
    if (id === 'feedback')     return feedbackCount    || 0;
    return 0;
  };

  const getSubLabel = (id) => {
    if (id === 'ships') return projectHint(activeProject) || undefined;
    return undefined;
  };

  return (
    <div className="activity-bar">
      <div className="activity-bar-top">
        {SECTIONS.map(s => (
          <SectionBtn key={s.id} {...s} active={activeSection === s.id}
            onClick={() => onSectionChange(s.id)} badge={getBadge(s.id)}
            subLabel={getSubLabel(s.id)} />
        ))}
      </div>

      <div className="activity-bar-mobile">
        {MOBILE_MAIN.map(s => (
          <SectionBtn key={s.id} {...s} active={activeSection === s.id}
            onClick={() => onSectionChange(s.id)} badge={getBadge(s.id)}
            subLabel={getSubLabel(s.id)} />
        ))}
        <button className={`activity-bar-btn${menuOpen ? ' active' : ''}`} title="More"
          onClick={() => setMenuOpen(o => !o)}>
          <VscMenu size={22} />
          {MOBILE_OVERFLOW.some(s => getBadge(s.id) > 0) && (
            <span className="activity-badge" style={{ top: 8, right: 8 }}>
              {MOBILE_OVERFLOW.reduce((s, sec) => s + getBadge(sec.id), 0)}
            </span>
          )}
        </button>
      </div>

      <div className="activity-bar-bottom">
        <button className="activity-bar-btn" title="Settings" onClick={onOpenSettings}>
          <VscSettingsGear size={24} />
        </button>
        <button className="activity-bar-btn" title="Sign Out" onClick={onLogout}>
          <VscSignOut size={22} />
        </button>
      </div>

      {menuOpen && (
        <>
          <div className="mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="mobile-menu">
            {MOBILE_OVERFLOW.map(({ id, Icon, title, color }) => (
              <button key={id}
                className={`mobile-menu-item${activeSection === id ? ' active' : ''}`}
                style={{ '--section-color': color }}
                onClick={() => { onSectionChange(id); setMenuOpen(false); }}>
                <Icon size={18} style={{ color: activeSection === id ? color : undefined }} />
                <span>{title}</span>
              </button>
            ))}
            {MOBILE_OVERFLOW.length > 0 && <div className="mobile-menu-divider" />}
            <button className="mobile-menu-item" onClick={() => { onOpenSettings(); setMenuOpen(false); }}>
              <VscSettingsGear size={18} />
              <span>Settings</span>
            </button>
            <button className="mobile-menu-item" onClick={() => { onLogout(); setMenuOpen(false); }}>
              <VscSignOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ActivityBar;
